import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, Input, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { IAssessmentExtended, IProfile, IResponse } from '@interfaces/index';
import { debounceTime, finalize, forkJoin, switchMap } from 'rxjs';
import { AppService } from '@ui-components/services';
import { AssessmentService } from '../../services/assessment.service';
import { DashboardService } from '../../services/dashboard.service';
import { ReportDownloadService } from '../../services/report-download.service';

@Component({
  selector: 'app-team-assessments',
  templateUrl: './team-assessments.component.html',
  styleUrl: './team-assessments.component.scss'
})
export class TeamAssessmentsComponent {
  STATUS_DEFAULT = 'Status';

  STATUS = {
    OPEN: 'Open',
    DONE: 'Done',
    OVERDUE: 'Overdue'
  }

  COLUMNS = {
    CHECKBOX: 'checkbox',
    NAME: 'name',
    ASSESSMENT: 'assessment',
    STATUS: 'status',
    ASSIGNED: 'assigned',
    COMPLETED: 'completed',
    ITERATION: 'iteration',
    WORKPLACE: 'workplace',
    COMPANY: 'company',
    COHORT: 'cohort',
    ACTIONS: 'actions'
  }

  MY_COLUMN_KEYS = Object.values(this.COLUMNS);

  TEAM_ASSESSMENT_COLUMNS_MOBILE_VIEW = {
    NAME: 'name',
    ASSESSMENT: 'assessment',
    STATUS: 'status',
    ACTIONS: 'actions'
  }

  TEAM_ASSESSMENT_COLUMNS_MOBILE_VIEW_KEYS = Object.values(this.TEAM_ASSESSMENT_COLUMNS_MOBILE_VIEW);

  applied_status = this.STATUS_DEFAULT;
  downloadingId: number | null = null;
  filtered_assessments: IAssessmentExtended[] = [];
  form: FormGroup;
  isExpanded: boolean = false;
  isMobileView: boolean = false;
  isStatusMenuVisible: boolean = false;
  loading = false;
  noResults = false;
  selected_status: string = '';
  search: string | null = null;
  sorted_data: IAssessmentExtended[] = [];
  team_assessments: IAssessmentExtended[] = [];
  selection: IAssessmentExtended[] = [];

  @Input() profile: IProfile | null = null;
  @ViewChild('statusChip') statusChip!: ElementRef;
  @ViewChild('statusMenu') statusMenu!: ElementRef;
  @ViewChildren(MatMenuTrigger) triggers!: QueryList<MatMenuTrigger>;
  private destroyRef: DestroyRef;
  private storageKey = 'teamAssessmentSearchQuery';

  constructor(
    protected _app_service: AppService,
    protected _assessment_service: AssessmentService,
    protected _breakpointObserver: BreakpointObserver,
    protected _dashboard_service: DashboardService,
    protected _fb: FormBuilder,
    protected _report_download_service: ReportDownloadService,
    protected _router: Router,
    destroyRef: DestroyRef
  ) {
    const storedSearch = localStorage.getItem(this.storageKey);
    this.search = storedSearch ? storedSearch : null;
    this.form = this._fb.group({
      search: [this.search]
    });
    this.destroyRef = destroyRef;
  }

  // Super Admin (1), Org Admin (2) and Cohort Admin (4) see the team list; the API rejects other roles with a 403.
  get canViewTeam(): boolean {
    return !!this.profile && [1, 2, 4].includes(this.profile.role_id);
  }

  ngOnInit(): void {
    if (!this.canViewTeam) return;

    this._app_service.selectedManagementGroup$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.profile) {
          this.team_assessments = [];
          this.sorted_data = [];
          this.noResults = false;
          this.loadData(this.profile);
        }
      });

    if (this.profile) {
      this.loadData(this.profile);

      this.form.valueChanges.pipe(
        debounceTime(800),
        switchMap(() => {
          this.search = this.form.get('search')?.value === '' ? null : this.form.get('search')?.value;
          if (this.search) {
            localStorage.setItem(this.storageKey, this.search);
          } else {
            localStorage.removeItem(this.storageKey);
          }
          return this._assessment_service.getTeamAssessments(this.profile!.org_id, this.search);
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(
        (response: IResponse<IAssessmentExtended[]>) => {
          this.handleAssessmentResponse(response);
        }
      );
    }
  }

  loadData(profile: IProfile): void {
    this.loading = true;
    this._breakpointObserver.observe([Breakpoints.Handset]).subscribe(result => {
      this.isMobileView = result.matches;
    });

    this._assessment_service.getTeamAssessments(profile.org_id, this.search)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: IResponse<IAssessmentExtended[]>) => {
        this.handleAssessmentResponse(response);
      });
  }

  handleAssessmentResponse(response: IResponse<IAssessmentExtended[]>) {
    if (response.code === 200) {
      if (response.data.length > 0) {
        this.team_assessments = response.data.filter(this.displayAssessments);
        this.sorted_data = this.team_assessments.slice();
        this.filtered_assessments = this.sorted_data;
        this.noResults = false;
      } else {
        this.noResults = true;
      }
    } else {
      this.noResults = true;
    }
    this.sorted_data = this.team_assessments.slice();
    this.loading = false;
  }

  approve(assessment: IAssessmentExtended): void {
    this._assessment_service.approve(assessment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._app_service.notify('Your request was successful', 'Success');
        if (this.profile) {
          this.loadData(this.profile);
        }
      });
  }

  approveInBulk() {
    this.approveSelected(this.selection);
    this.selection = [];
  }

  approveSelected(assessments: IAssessmentExtended[]): void {
    if (assessments.length === 0) {
      this._app_service.notify('No assessments selected.', 'warning');
      return;
    }

    const completed = assessments.filter(check => check.completed_at && check.require_approval === 1 && !check.approved_at);

    if (completed.length > 0) {
      const requests = completed.map(assessment =>
        this._assessment_service.approve(assessment).pipe(
          takeUntilDestroyed(this.destroyRef)
        )
      );
      // Leveraging current endpoint. 
      // TODO: OWL-507 Move logic to the backend to avoid multiple API calls.
      forkJoin(requests).subscribe(() => {
        this._app_service.notify(
          `Approved ${requests.length} assessments.`,
          'success'
        );
      });
    } else {
      this._app_service.notify(
        `Your selected assessments are already approved.`,
        'success'
      );
    }
  }

  cancel(assessment: IAssessmentExtended): void {
    this._assessment_service.cancel(assessment)
      .pipe(
        switchMap(() => this._assessment_service.getTeamAssessments(assessment.org_id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: IResponse<IAssessmentExtended[]>) => {
        this.team_assessments = response.data.filter(this.displayAssessments);
      });
  }

  assign(assessment: IAssessmentExtended): void {
    const org_id = assessment.org_id;
    const assessment_id = assessment.assessment_id
    const person_id = assessment.person_id;
    const url = `invite/?org_id=${encodeURIComponent(org_id)}` + 
                `&assessment_id=${encodeURIComponent(assessment_id)}` +
                `&person_id=${encodeURIComponent(person_id)}`;
    window.location.href = url;
  }

  clearFilters(): void {
    this.form.patchValue({ search: '' });
    this._assessment_service.getTeamAssessments(this.profile?.org_id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: IResponse<IAssessmentExtended[]>) => {
        this.sorted_data = response.data;
        this.applied_status = this.STATUS_DEFAULT;
      });
    this.isStatusMenuVisible = false;
  }

  clearSearch() {
    this.form.reset();
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    const clickedElement = event.target as HTMLElement;

    if (
      this.isStatusMenuVisible &&
      !this.statusMenu.nativeElement.contains(clickedElement) &&
      !this.statusChip.nativeElement.contains(clickedElement)
    ) {
      this.isStatusMenuVisible = false;
    }
  }

  complete(assessment: IAssessmentExtended) {
    this._dashboard_service.complete(assessment);
  }

  displayAssessments(assessment: IAssessmentExtended): boolean {
    // Currently cannot tell if uncompleted B2C assessments are paid or not.
    return !(assessment.cohort_name === 'B2C' && !assessment.completed_at);
  }

  downloadReport(assessment: IAssessmentExtended) {
    this.downloadingId = assessment.org_person_assessment_id;
    this._report_download_service.downloadReport(assessment.org_person_assessment_id).pipe(
      finalize(() => this.downloadingId = null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      error: () => this._app_service.notify('Could not prepare the report — try again.', 'error')
    });
  }

  getAssessmentStatus(ref: IAssessmentExtended): string {
    return new Date(ref.completed_at).toLocaleDateString();
  }

  getOverdueDays(days: number): number {
    return Math.abs(days);
  }

  goToProfile(person_id: number) {
    this._router.navigate(['/profile/basic', person_id]);
  }

  onCheck(event: any, assessment: IAssessmentExtended) {
    if (event.checked) {
      this.selection.push(assessment);
    } else {
      this.selection = this.selection.filter(s => s.person_id !== assessment.person_id)
    }
  }

  onOptionChange(option: string): void {
    this.selected_status = option;
  }

  reminder(assessments: IAssessmentExtended[]): void {
    if (assessments.length === 0) {
      this._app_service.notify('No assessments selected.', 'warning');
      return;
    }

    this._assessment_service.reminder(assessments)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: IResponse<{}>) => {
        if (response.code === 200) {
          this._app_service.notify(`Sent reminder to ${assessments.length} users.`, 'success')
        } else {
          this._app_service.notify(response.message!, 'error')
        }
      });
  }

  reminderInBulk() {
    this.reminder(this.selection);
    this.selection = [];
  }


  renewAssignment(assessment: IAssessmentExtended): void {
    this._assessment_service.renew(assessment)
      .pipe(
        switchMap(() => this._assessment_service.getTeamAssessments(assessment.org_id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: IResponse<IAssessmentExtended[]>) => {
        this.team_assessments = response.data.filter(this.displayAssessments);
      });
  }

  selectAll(event: any) {
    if (event.checked) {
      this.selection = [...this.sorted_data];
    } else {
      this.selection = [];
    }
  }
  isAllSelected(): boolean {
    return this.selection.length === this.sorted_data.length;
  }

  showStatusMenu() {
    this.isStatusMenuVisible = !this.isStatusMenuVisible;
  }

  sortData(sort: Sort) {
    const data = this.team_assessments.slice();
    if (!sort.active || sort.direction === '') {
      this.sorted_data = data;
      return;
    }

    this.sorted_data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name':
          const firstNameComparison = this.compare(a.first_name, b.first_name, isAsc);
          return firstNameComparison !== 0 ? firstNameComparison : this.compare(a.last_name, b.last_name, isAsc);
        case 'company':
          return this.compare(a.org_name, b.org_name, isAsc);
        case 'cohort':
          return this.compare(a.cohort_name, b.cohort_name, isAsc);
        case 'assigned':
          return this.compare(a.created_at, b.created_at, isAsc);
        case 'completed':
          return this.compare(a.completed_at, b.completed_at, isAsc);
        case 'workplace':
          return this.compare(a.workplace, b.workplace, isAsc);
        default:
          return 0;
      }
    });
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  statusFilter(): void {
    this._assessment_service.getTeamAssessments(this.profile?.org_id!, this.search)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: IResponse<IAssessmentExtended[]>) => {
        this.sorted_data = response.data;

        if (this.selected_status === this.STATUS.OPEN) {
          this.filtered_assessments = this.sorted_data.filter(assessment => assessment.days_remaining! >= 0 && !assessment.completed_at);
        } else if (this.selected_status === this.STATUS.DONE) {
          this.filtered_assessments = this.sorted_data.filter(assessment => assessment.completed_at);
        } else if (this.selected_status === this.STATUS.OVERDUE) {
          this.filtered_assessments = this.sorted_data.filter(assessment => assessment.days_remaining! < 0);
        }

        this.applied_status = this.selected_status;
        this.isStatusMenuVisible = false;
        this.sorted_data = this.filtered_assessments;
      });
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  view(assessment: IAssessmentExtended) {
    this._dashboard_service.view(assessment);
  }

  viewRow(assessment: IAssessmentExtended, rowIndex: number) {
    if (assessment.completed_at) {
      this.view(assessment);
    } else {
      this.triggers.forEach((trigger, index) => {
        if (trigger.menuOpen && index !== rowIndex) {
          trigger.closeMenu();
        }
      });
      const trigger = this.triggers.toArray()[rowIndex];
      if (trigger && !trigger.menuOpen) {
        trigger.openMenu();
      }
    }
  }
}
